import type { ArdoqId } from '@ardoq/api-types';
import type { GraphItemId, RawGraphNode2 } from '../types';

type NodeMaps = {
  componentMap: Map<GraphItemId, RawGraphNode2>;
  groupMap: Map<GraphItemId, RawGraphNode2>;
};

type PruneToStartSetBranchesArgs = NodeMaps & {
  rootItems: RawGraphNode2[];
  startSet: ArdoqId[];
};

type PruneToStartSetBranchesResult = NodeMaps & {
  rootItems: RawGraphNode2[];
};

type ResolveNode = (node: RawGraphNode2) => RawGraphNode2;

const createResolveNode =
  ({ componentMap, groupMap }: NodeMaps): ResolveNode =>
  node =>
    groupMap.get(node.id) ?? componentMap.get(node.id) ?? node;

const collectStartSetBranchIds = (
  node: RawGraphNode2,
  startSetIds: Set<ArdoqId>,
  resolveNode: ResolveNode
): GraphItemId[] => {
  const resolvedNode = resolveNode(node);
  const childIds = (resolvedNode.children ?? []).flatMap(child =>
    collectStartSetBranchIds(child, startSetIds, resolveNode)
  );
  const isStartSetComponent = Boolean(
    resolvedNode.modelId && startSetIds.has(resolvedNode.modelId)
  );
  if (!isStartSetComponent && childIds.length === 0) {
    return [];
  }
  return [resolvedNode.id, ...childIds];
};

const pruneChildren = (
  node: RawGraphNode2,
  keptIds: Set<GraphItemId>
): RawGraphNode2 =>
  node.children
    ? { ...node, children: node.children.filter(({ id }) => keptIds.has(id)) }
    : node;

const pruneTree = (
  node: RawGraphNode2,
  keptIds: Set<GraphItemId>
): RawGraphNode2 =>
  node.children
    ? {
        ...node,
        children: node.children
          .filter(({ id }) => keptIds.has(id))
          .map(child => pruneTree(child, keptIds)),
      }
    : node;

const pruneMap = (
  nodeMap: Map<GraphItemId, RawGraphNode2>,
  keptIds: Set<GraphItemId>
): Map<GraphItemId, RawGraphNode2> =>
  new Map(
    Array.from(nodeMap, ([id, node]) => [id, pruneChildren(node, keptIds)])
  );

/**
 * Removes the members of the grouping hierarchy which are neither a start set
 * component nor an ancestor of one.
 */
export const pruneToStartSetBranches = ({
  rootItems,
  startSet,
  componentMap,
  groupMap,
}: PruneToStartSetBranchesArgs): PruneToStartSetBranchesResult => {
  const resolveNode = createResolveNode({ componentMap, groupMap });
  const startSetIds = new Set(startSet);
  const keptIds = new Set(
    rootItems.flatMap(rootItem =>
      collectStartSetBranchIds(rootItem, startSetIds, resolveNode)
    )
  );

  return {
    rootItems: rootItems
      .filter(({ id }) => keptIds.has(id))
      .map(rootItem => pruneTree(rootItem, keptIds)),
    componentMap: pruneMap(componentMap, keptIds),
    groupMap: pruneMap(groupMap, keptIds),
  };
};
